import React, { useState, useMemo } from 'react';
import { db } from '../data/store';

const COLORS = {
  Fuel: '#e09900', Specific: '#1a6b42', Water: '#1a7dc4',
  Coal: '#555', Stormwater: '#2196f3', NPDES: '#9c27b0',
  TCLP: '#f44336', 'Aquatic Toxicity': '#00bcd4',
  Wells: '#795548', 'QA/QC': '#607d8b', Default: '#1e3060'
};
const color = (t) => COLORS[t] || COLORS.Default;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const companyName = (sample) => {
  const source = String(sample?.source || '').trim();
  const client = String(sample?.client || '').trim();
  return source || client || 'Unknown';
};

// ── Sub-components ────────────────────────────────────────────────────────────
function MiniBar({ label, value, max, col, onClick }) {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5,
      cursor:onClick?'pointer':'default'}} onClick={onClick}>
      <span style={{width:130,fontSize:11,color:'#444',textAlign:'right',flexShrink:0,
        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={label}>{label}</span>
      <div style={{flex:1,background:'#f0f2f8',borderRadius:10,height:18,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${pct}%`,background:col||'#1e3060',borderRadius:10,
          display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:6,
          transition:'width 0.6s ease',minWidth:value>0?28:0}}>
          {pct>15 && <span style={{fontSize:10,fontWeight:700,color:'white'}}>{value}</span>}
        </div>
      </div>
      {pct<=15 && <span style={{fontSize:10,fontWeight:700,color:'#333',minWidth:24}}>{value}</span>}
    </div>
  );
}

function KPI({ label, value, sub, color: c, icon, trend }) {
  return (
    <div style={{background:'white',border:'1px solid #e2e6f0',borderRadius:8,padding:'12px 16px',
      boxShadow:'0 1px 4px rgba(0,0,0,0.06)',borderTop:`3px solid ${c||'#1e3060'}`}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontSize:24,fontWeight:700,color:c||'#1e3060',lineHeight:1.1}}>{value}</div>
          <div style={{fontSize:11,color:'#888',marginTop:3,textTransform:'uppercase',letterSpacing:'0.4px'}}>{label}</div>
          {sub && <div style={{fontSize:11,color:'#666',marginTop:2}}>{sub}</div>}
        </div>
        <div style={{fontSize:24,opacity:0.4}}>{icon}</div>
      </div>
      {trend !== undefined && (
        <div style={{marginTop:6,fontSize:11,color:trend>=0?'#1a6b42':'#b83232',fontWeight:600}}>
          {trend>=0?'▲':'▼'} {Math.abs(trend)}% vs last month
        </div>
      )}
    </div>
  );
}

function MonthlyChart({ data, height=120 }) {
  const max = Math.max(...data.map(d=>d.total),1);
  return (
    <div style={{display:'flex',alignItems:'flex-end',gap:4,height,paddingTop:8}}>
      {data.map((d,i) => (
        <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
          <span style={{fontSize:9,fontWeight:700,color:'#1e3060'}}>{d.total||''}</span>
          <div style={{width:'100%',display:'flex',flexDirection:'column-reverse',
            borderRadius:'3px 3px 0 0',overflow:'hidden',minHeight:4}}>
            {Object.entries(d.byType||{}).map(([type,cnt]) => (
              <div key={type} title={`${type}: ${cnt}`}
                style={{width:'100%',height:`${Math.round(cnt/max*(height-24))}px`,
                  background:color(type),minHeight:cnt>0?3:0}}/>
            ))}
          </div>
          <span style={{fontSize:9,color:'#888'}}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Drill-down Modal ──────────────────────────────────────────────────────────
function DrillModal({ modal, onClose }) {
  if (!modal) return null;
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',
      alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={onClose}>
      <div style={{background:'white',borderRadius:8,width:860,maxHeight:'85vh',
        display:'flex',flexDirection:'column',boxShadow:'0 12px 40px rgba(0,0,0,0.3)'}}
        onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{background:'#1e3060',color:'white',padding:'12px 18px',
          display:'flex',alignItems:'center',justifyContent:'space-between',
          borderRadius:'8px 8px 0 0',flexShrink:0}}>
          <div>
            <div style={{fontSize:15,fontWeight:700}}>{modal.title}</div>
            <div style={{fontSize:11,color:'#aac4e0',marginTop:2}}>{modal.subtitle}</div>
          </div>
          <button onClick={onClose}
            style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',
              cursor:'pointer',fontSize:22,lineHeight:1,padding:0}}>✕</button>
        </div>
        {/* Body */}
        <div style={{overflowY:'auto',flex:1}}>
          {modal.samples.length===0 ? (
            <div style={{textAlign:'center',padding:40,color:'#999',fontSize:13}}>
              No samples found for this filter
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#f0f4ff',position:'sticky',top:0,zIndex:1}}>
                  {['Sample #','Work Order','Company / Source','Description','Test Type',
                    'Received','Due','Status','Rush','Price'].map(h=>(
                    <th key={h} style={{padding:'7px 10px',textAlign:'left',
                      borderBottom:'2px solid #e2e6f0',color:'#1e3060',
                      fontWeight:600,fontSize:11,whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modal.samples.map((s,i)=>{
                  const overdue = !s.completed && s.dueDate && new Date(s.dueDate)<new Date();
                  return (
                    <tr key={s.id} style={{borderBottom:'1px solid #f0f2f8',
                      background:overdue?'#fff5f5':i%2===0?'white':'#fafbff'}}>
                      <td style={{padding:'6px 10px',fontWeight:700,
                        fontFamily:'monospace',color:'#1e3060'}}>{s.id}</td>
                      <td style={{padding:'6px 10px',fontFamily:'monospace',
                        color:'#666'}}>{s.workOrder}</td>
                      <td style={{padding:'6px 10px',maxWidth:130,overflow:'hidden',
                        textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{companyName(s)}</td>
                      <td style={{padding:'6px 10px',maxWidth:160,overflow:'hidden',
                        textOverflow:'ellipsis',whiteSpace:'nowrap',fontSize:11,
                        color:'#555'}}>{s.desc}</td>
                      <td style={{padding:'6px 10px'}}>
                        <span style={{background:(COLORS[s.testType]||COLORS.Default)+'22',
                          color:COLORS[s.testType]||COLORS.Default,
                          padding:'2px 8px',borderRadius:10,fontSize:10,
                          fontWeight:700}}>{s.testType}</span>
                      </td>
                      <td style={{padding:'6px 10px',fontSize:11,color:'#666'}}>{s.received}</td>
                      <td style={{padding:'6px 10px',fontSize:11,
                        color:overdue?'#b83232':'#666',fontWeight:overdue?700:400}}>
                        {s.dueDate||'—'}{overdue?' ⚠️':''}
                      </td>
                      <td style={{padding:'6px 10px'}}>
                        <span style={{background:s.completed?'#d4edda':'#fff3cd',
                          color:s.completed?'#155724':'#856404',
                          padding:'2px 8px',borderRadius:10,fontSize:10,fontWeight:700}}>
                          {s.completed?'Complete':'Pending'}
                        </span>
                      </td>
                      <td style={{padding:'6px 10px',textAlign:'center'}}>
                        {s.rush ? (
                          <span style={{background:'#b83232',color:'white',borderRadius:10,
                            padding:'1px 7px',fontSize:10,fontWeight:700}}>{s.rush}</span>
                        ) : '—'}
                      </td>
                      <td style={{padding:'6px 10px',fontWeight:700,
                        color:'#1a6b42'}}>${(s.price||0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {/* Footer */}
        <div style={{padding:'10px 18px',borderTop:'1px solid #e2e6f0',background:'#f8f9ff',
          borderRadius:'0 0 8px 8px',display:'flex',
          justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <span style={{fontSize:12,color:'#666',fontWeight:600}}>
            {modal.samples.length} sample{modal.samples.length!==1?'s':''} shown
          </span>
          <button onClick={onClose}
            style={{background:'#1e3060',color:'white',border:'none',borderRadius:4,
              padding:'6px 18px',cursor:'pointer',fontSize:12,fontWeight:600}}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function OperationsDashboard({ onNavigate }) {
  const [timeRange, setTimeRange] = useState('12m');
  const [dateRange, setDateRange] = useState('all');
  const [drillModal, setDrillModal] = useState(null);
  const allSamples = db.getSamples();
  const now = new Date();

  const DATE_RANGES = [
    { id:'1d',  label:'1 Day',     days:1   },
    { id:'7d',  label:'7 Days',    days:7   },
    { id:'14d', label:'14 Days',   days:14  },
    { id:'30d', label:'30 Days',   days:30  },
    { id:'3m',  label:'3 Months',  days:90  },
    { id:'6m',  label:'6 Months',  days:180 },
    { id:'12m', label:'12 Months', days:365 },
    { id:'all', label:'All Time',  days:null},
  ];

  const filteredSamples = useMemo(() => {
    const range = DATE_RANGES.find(r=>r.id===dateRange);
    if (!range||!range.days) return allSamples;
    const cutoff = new Date(now.getTime() - range.days*86400000);
    return allSamples.filter(s=>s.received && new Date(s.received)>=cutoff);
  }, [allSamples, dateRange]);

  const dateRangeLabel = DATE_RANGES.find(r=>r.id===dateRange)?.label || 'All Time';

  const openDrill = (title, subtitle, samples) =>
    setDrillModal({ title, subtitle, samples });

  // ── Metrics ─────────────────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const total = filteredSamples.length;
    const completed = filteredSamples.filter(s=>s.completed).length;
    const pending = filteredSamples.filter(s=>!s.completed).length;
    const rush = filteredSamples.filter(s=>s.rush&&s.rush<=3).length;
    const thisMonth = filteredSamples.filter(s=>{
      if(!s.received) return false;
      const d=new Date(s.received);
      return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
    }).length;
    const lastMonthDate = new Date(now.getFullYear(),now.getMonth()-1,1);
    const lastMonth = filteredSamples.filter(s=>{
      if(!s.received) return false;
      const d=new Date(s.received);
      return d.getMonth()===lastMonthDate.getMonth()&&d.getFullYear()===lastMonthDate.getFullYear();
    }).length;
    const trend = lastMonth>0?Math.round((thisMonth-lastMonth)/lastMonth*100):0;
    const withDates = filteredSamples.filter(s=>s.completed&&s.received&&s.dateCompleted);
    const avgTurnaround = withDates.length>0
      ? Math.round(withDates.reduce((a,s)=>a+(new Date(s.dateCompleted)-new Date(s.received))/86400000,0)/withDates.length)
      : 0;
    return {total,completed,pending,rush,thisMonth,trend,avgTurnaround};
  }, [filteredSamples]);

  // ── Monthly data ─────────────────────────────────────────────────────────
  const monthCount = timeRange==='12m'?12:timeRange==='6m'?6:24;
  const monthlyData = useMemo(() => {
    return Array.from({length:monthCount},(_,i)=>{
      const d=new Date(now.getFullYear(),now.getMonth()-(monthCount-1)+i,1);
      const label=MONTHS[d.getMonth()]+(monthCount>12?` '${String(d.getFullYear()).slice(2)}`:'');
      const inMonth=filteredSamples.filter(s=>{
        if(!s.received) return false;
        const sd=new Date(s.received);
        return sd.getMonth()===d.getMonth()&&sd.getFullYear()===d.getFullYear();
      });
      const byType={};
      inMonth.forEach(s=>{byType[s.testType]=(byType[s.testType]||0)+1;});
      return {label,total:inMonth.length,completed:inMonth.filter(s=>s.completed).length,byType};
    });
  }, [filteredSamples,monthCount]);

  // ── By company ───────────────────────────────────────────────────────────
  const byCompany = useMemo(()=>{
    const map={};
    filteredSamples.forEach(s=>{const c=companyName(s);map[c]=(map[c]||0)+1;});
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,12);
  },[filteredSamples]);
  const maxCompany = byCompany[0]?.[1]||1;

  // ── By test type ─────────────────────────────────────────────────────────
  const byType = useMemo(()=>{
    const map={};
    filteredSamples.forEach(s=>{map[s.testType]=(map[s.testType]||0)+1;});
    return Object.entries(map).sort((a,b)=>b[1]-a[1]);
  },[filteredSamples]);
  const maxType = byType[0]?.[1]||1;
  const totalTyped = byType.reduce((a,[,n])=>a+n,0);

  // ── Pending by company ───────────────────────────────────────────────────
  const pendingByCompany = useMemo(()=>{
    const map={};
    filteredSamples.filter(s=>!s.completed).forEach(s=>{
      const c=companyName(s);map[c]=(map[c]||0)+1;
    });
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,8);
  },[filteredSamples]);

  // ── By assigned person ─────────────────────────────────────────────────────
  const byPerson = useMemo(()=>{
    const map={};
    filteredSamples.forEach(s=>{
      const p = s.tech || 'Unassigned';
      if(!map[p]) map[p]={ total:0, completed:0, pending:0, rush:0 };
      map[p].total++;
      if(s.completed) map[p].completed++;
      else map[p].pending++;
      if(s.rush && parseInt(s.rush)<=3) map[p].rush++;
    });
    return Object.entries(map).sort((a,b)=>b[1].total-a[1].total);
  },[filteredSamples]);

  // ── Completion by type ───────────────────────────────────────────────────
  const completionByType = useMemo(()=>{
    const map={};
    filteredSamples.forEach(s=>{
      if(!map[s.testType]) map[s.testType]={total:0,done:0};
      map[s.testType].total++;
      if(s.completed) map[s.testType].done++;
    });
    return Object.entries(map)
      .map(([t,{total,done}])=>([t,Math.round(done/total*100),total]))
      .sort((a,b)=>b[1]-a[1]);
  },[filteredSamples]);

  // ── Rush breakdown ───────────────────────────────────────────────────────
  const rushBreakdown = useMemo(()=>{
    const map={};
    filteredSamples.filter(s=>s.rush).forEach(s=>{map[s.rush]=(map[s.rush]||0)+1;});
    return Object.entries(map).sort((a,b)=>a[0]-b[0]);
  },[filteredSamples]);

  // ── YTD ──────────────────────────────────────────────────────────────────
  const ytdComparison = useMemo(()=>{
    const thisYear=now.getFullYear();
    const ytd=allSamples.filter(s=>s.received&&new Date(s.received).getFullYear()===thisYear).length;
    const prior=allSamples.filter(s=>s.received&&new Date(s.received).getFullYear()===thisYear-1).length;
    return {ytd,prior,growth:prior>0?Math.round((ytd-prior)/prior*100):0};
  },[allSamples]);

  const cardStyle = {background:'white',border:'1px solid #e2e6f0',borderRadius:8,
    overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.05)'};
  const hdr = (label,action) => (
    <div style={{background:'#1e3060',color:'white',padding:'8px 14px',fontSize:12,
      fontWeight:600,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
      <span>{label}</span>
      {action && <span style={{fontSize:10,fontWeight:400,color:'#aac4e0'}}>{action}</span>}
    </div>
  );

  const rushLabels = {1:'Same Day',2:'Next Day',3:'2-3 Days',4:'4-5 Days',5:'Standard'};
  const rushCols   = {1:'#b83232',2:'#e05500',3:'#e09900',4:'#4caf50',5:'#1e3060'};

  return (
    <div style={{overflowY:'auto',height:'100%',padding:'14px 16px',background:'#eef0f5'}}>

      {/* ── Header ── */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
        <div>
          <div style={{fontSize:18,fontWeight:700,color:'#1e3060'}}>Operations Dashboard</div>
          <div style={{fontSize:11,color:'#888'}}>Sample workflow, completion status &amp; testing analytics</div>
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:10,alignItems:'center'}}>
          <span style={{fontSize:11,color:'#666',fontWeight:600}}>Date Range:</span>
          <select value={dateRange} onChange={e=>setDateRange(e.target.value)}
            style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 10px',
              fontSize:12,outline:'none',fontWeight:600,color:'#1e3060',cursor:'pointer',
              background:'white',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
            {DATE_RANGES.map(r=>(
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
          <div style={{width:1,height:20,background:'#ddd'}}/>
          <span style={{fontSize:11,color:'#666',fontWeight:600}}>Chart:</span>
          {[['6m','6 Mo'],['12m','12 Mo'],['24m','2 Yr']].map(([v,l])=>(
            <button key={v} onClick={()=>setTimeRange(v)}
              style={{padding:'4px 10px',borderRadius:4,border:'1px solid #cdd3e4',
                fontSize:11,cursor:'pointer',fontWeight:600,
                background:timeRange===v?'#1e3060':'white',
                color:timeRange===v?'white':'#1e3060'}}>
              {l}
            </button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={()=>onNavigate('receive')}>＋ New Sample</button>
      </div>

      {/* ── KPI row ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',
        gap:10,marginBottom:14}}>
        <KPI label="Total Samples" value={metrics.total} icon="🔬" color="#1e3060"/>
        <KPI label="Completed" value={metrics.completed} icon="✅" color="#1a6b42"
          sub={`${metrics.total>0?Math.round(metrics.completed/metrics.total*100):0}% rate`}/>
        <KPI label="Pending" value={metrics.pending} icon="⏳" color="#e09900"/>
        <KPI label="This Month" value={metrics.thisMonth} icon="📅" color="#1a7dc4" trend={metrics.trend}/>
        <KPI label="Avg Turnaround" value={`${metrics.avgTurnaround}d`} icon="⏱" color="#9c27b0"/>
        <KPI label="Rush Samples" value={metrics.rush} icon="🚨" color="#b83232"
          sub={`${metrics.total>0?Math.round(metrics.rush/metrics.total*100):0}% of total`}/>
      </div>

      {/* ── Row 1: Monthly chart + type bars ── */}
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
        <div style={cardStyle}>
          {hdr(`Samples Received — Monthly`,
            timeRange==='12m'?'Last 12 Months':timeRange==='6m'?'Last 6 Months':'Last 2 Years')}
          <div style={{padding:'12px 14px'}}>
            <MonthlyChart data={monthlyData} height={140}/>
            <div style={{display:'flex',flexWrap:'wrap',gap:'4px 14px',marginTop:8}}>
              {byType.map(([t])=>(
                <div key={t} style={{display:'flex',alignItems:'center',gap:4,fontSize:10}}>
                  <div style={{width:10,height:10,borderRadius:2,background:color(t),flexShrink:0}}/>
                  <span style={{color:'#555'}}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          {hdr('Samples by Test Type')}
          <div style={{padding:'10px 14px'}}>
            {byType.map(([t,n])=>(
              <div key={t} style={{marginBottom:6,cursor:'pointer'}}
                onClick={()=>openDrill(`${t} — All Samples`,
                  `${n} sample${n!==1?'s':''} · ${dateRangeLabel}`,
                  filteredSamples.filter(s=>s.testType===t))}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:2}}>
                  <span style={{color:'#333',fontWeight:500}}>{t}</span>
                  <span style={{color:'#888'}}>{n} ({Math.round(n/totalTyped*100)}%)</span>
                </div>
                <div style={{background:'#f0f2f8',borderRadius:10,height:8,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${Math.round(n/maxType*100)}%`,
                    background:color(t),borderRadius:10,transition:'width 0.5s'}}/>
                </div>
              </div>
            ))}
            <div style={{marginTop:6,fontSize:10,color:'#aaa',fontStyle:'italic'}}>
              Click any row to see samples
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: By company + pending ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <div style={cardStyle}>
          {hdr('Top Companies by Sample Volume', dateRangeLabel)}
          <div style={{padding:'10px 14px'}}>
            {byCompany.map(([c,n])=>(
              <MiniBar key={c} label={c} value={n} max={maxCompany} col="#1e3060"
                onClick={()=>openDrill(`Samples — ${c}`,
                  `${n} sample${n!==1?'s':''} · ${dateRangeLabel}`,
                  filteredSamples.filter(s=>companyName(s)===c))}/>
            ))}
            <div style={{marginTop:4,fontSize:10,color:'#aaa',fontStyle:'italic'}}>
              Click a company to see its samples
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          {hdr('Pending Work by Company', `${metrics.pending} samples · ${dateRangeLabel}`)}
          <div style={{padding:'10px 14px'}}>
            {pendingByCompany.length===0
              ? <div style={{textAlign:'center',color:'#999',fontSize:12,padding:20}}>
                  🎉 All caught up — no pending samples
                </div>
              : pendingByCompany.map(([c,n])=>(
                <MiniBar key={c} label={c} value={n} max={pendingByCompany[0][1]} col="#e09900"
                  onClick={()=>openDrill(`Pending — ${c}`,
                    `${n} pending sample${n!==1?'s':''}`,
                    filteredSamples.filter(s=>companyName(s)===c&&!s.completed))}/>
              ))
            }
            {pendingByCompany.length>0 && (
              <div style={{marginTop:4,fontSize:10,color:'#aaa',fontStyle:'italic'}}>
                Click a company to see pending samples
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 2b: Samples by Assigned Person ── */}
      <div style={cardStyle} className="mb-12" key="personrow">
        {hdr('👤 Samples by Assigned Person', dateRangeLabel)}
        <div style={{padding:'12px 14px',overflowX:'auto'}}>
          {byPerson.length===0 ? (
            <div style={{color:'#999',fontSize:12,textAlign:'center',padding:12}}>No samples</div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead>
                <tr style={{background:'#f0f4ff'}}>
                  {['Assigned To','Total','Completed','Pending','Rush','Completion Rate','Workload'].map(h=>(
                    <th key={h} style={{padding:'5px 10px',textAlign: h==='Workload'||h==='Completion Rate'?'left':'center',
                      borderBottom:'1px solid #e2e6f0',color:'#1e3060',fontWeight:600,fontSize:11,
                      textAlign: h==='Assigned To'?'left':'center'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byPerson.map(([name,d],i)=>{
                  const pct = d.total>0 ? Math.round(d.completed/d.total*100) : 0;
                  const maxTotal = byPerson[0]?.[1]?.total||1;
                  return (
                    <tr key={name} style={{borderBottom:'1px solid #f0f2f8',
                      background:i%2===0?'white':'#fafbff'}}>
                      <td style={{padding:'6px 10px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{width:28,height:28,borderRadius:'50%',background:'#1e3060',
                            color:'white',fontSize:12,fontWeight:700,display:'flex',
                            alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            {name[0]?.toUpperCase()}
                          </div>
                          <span style={{fontWeight:600,color:'#1e3060'}}>{name}</span>
                        </div>
                      </td>
                      <td style={{padding:'6px 10px',textAlign:'center',fontWeight:700}}>{d.total}</td>
                      <td style={{padding:'6px 10px',textAlign:'center',color:'#1a6b42',fontWeight:600}}>{d.completed}</td>
                      <td style={{padding:'6px 10px',textAlign:'center',color:'#e09900',fontWeight:600}}>{d.pending}</td>
                      <td style={{padding:'6px 10px',textAlign:'center'}}>
                        {d.rush>0 ? (
                          <span style={{background:'#b83232',color:'white',borderRadius:10,
                            padding:'1px 8px',fontSize:10,fontWeight:700}}>{d.rush}</span>
                        ) : <span style={{color:'#ccc'}}>—</span>}
                      </td>
                      <td style={{padding:'6px 10px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <div style={{flex:1,background:'#f0f2f8',borderRadius:10,height:8,overflow:'hidden'}}>
                            <div style={{height:'100%',width:`${pct}%`,borderRadius:10,
                              background:pct>=80?'#1a6b42':pct>=50?'#e09900':'#b83232',
                              transition:'width 0.5s'}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:700,minWidth:32,
                            color:pct>=80?'#1a6b42':pct>=50?'#e09900':'#b83232'}}>
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td style={{padding:'6px 10px'}}>
                        <div style={{background:'#f0f2f8',borderRadius:10,height:14,
                          overflow:'hidden',minWidth:80}}>
                          <div style={{height:'100%',
                            width:`${Math.round(d.total/maxTotal*100)}%`,
                            background:'#1e3060',borderRadius:10,transition:'width 0.5s'}}/>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Row 3: Completion + Rush + YTD ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>

        <div style={cardStyle}>
          {hdr('Completion Rate by Test Type')}
          <div style={{padding:'10px 14px'}}>
            {completionByType.map(([t,pct,total])=>{
              const pendingCount = total - Math.round(total*pct/100);
              const barCol = pct>=80?'#1a6b42':pct>=50?'#e09900':'#b83232';
              return (
                <div key={t} style={{marginBottom:7}}>
                  <div style={{display:'flex',justifyContent:'space-between',
                    fontSize:11,marginBottom:2}}>
                    <span
                      style={{color:'#1e3060',fontWeight:600,cursor:'pointer',
                        textDecoration:'underline dotted'}}
                      title="Click to see pending samples"
                      onClick={()=>openDrill(
                        `${t} — Pending Samples`,
                        `${pendingCount} pending · ${pct}% complete`,
                        filteredSamples.filter(s=>s.testType===t&&!s.completed))}>
                      {t}
                    </span>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span
                        style={{fontSize:10,color:'#888',cursor:'pointer',
                          textDecoration:'underline dotted'}}
                        title="Click to see all samples"
                        onClick={()=>openDrill(
                          `${t} — All Samples`,
                          `${total} total · ${pct}% complete`,
                          filteredSamples.filter(s=>s.testType===t))}>
                        {total} total
                      </span>
                      <span style={{fontWeight:700,color:barCol}}>{pct}%</span>
                    </div>
                  </div>
                  <div style={{background:'#f0f2f8',borderRadius:10,height:6,cursor:'pointer'}}
                    onClick={()=>openDrill(
                      `${t} — Pending Samples`,
                      `${pendingCount} pending`,
                      filteredSamples.filter(s=>s.testType===t&&!s.completed))}>
                    <div style={{height:'100%',width:`${pct}%`,
                      background:barCol,borderRadius:10}}/>
                  </div>
                  <div style={{fontSize:9,color:'#aaa',marginTop:1}}>{total} samples</div>
                </div>
              );
            })}
            <div style={{marginTop:4,fontSize:10,color:'#aaa',fontStyle:'italic'}}>
              Click type name or bar to see samples
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          {hdr('Rush Priority Breakdown')}
          <div style={{padding:'14px'}}>
            {rushBreakdown.length===0
              ? <div style={{textAlign:'center',color:'#999',fontSize:12,padding:16}}>
                  No rush samples
                </div>
              : rushBreakdown.map(([level,count])=>(
                <div key={level}
                  onClick={()=>openDrill(
                    `Rush Level ${level} — ${rushLabels[level]||''}`,
                    `${count} sample${count!==1?'s':''} · click to view`,
                    filteredSamples.filter(s=>String(s.rush)===String(level)))}
                  style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,
                    padding:'8px 10px',borderRadius:6,background:'#f8f9ff',
                    border:'1px solid #e8ecf8',cursor:'pointer',transition:'all 0.15s'}}
                  onMouseEnter={e=>{
                    e.currentTarget.style.background='#e8f0ff';
                    e.currentTarget.style.borderColor=rushCols[level]||'#cdd3e4';
                  }}
                  onMouseLeave={e=>{
                    e.currentTarget.style.background='#f8f9ff';
                    e.currentTarget.style.borderColor='#e8ecf8';
                  }}>
                  <div style={{width:32,height:32,borderRadius:'50%',
                    background:rushCols[level]||'#1e3060',
                    display:'flex',alignItems:'center',justifyContent:'center',
                    color:'white',fontSize:14,fontWeight:700}}>
                    {level}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:'#1e3060'}}>
                      {rushLabels[level]||`Level ${level}`}
                    </div>
                    <div style={{fontSize:10,color:'#888'}}>
                      Rush level {level} · click to view samples
                    </div>
                  </div>
                  <div style={{fontSize:20,fontWeight:700,color:rushCols[level]||'#1e3060'}}>
                    {count}
                  </div>
                </div>
              ))
            }
          </div>
        </div>

        <div style={cardStyle}>
          {hdr('YTD vs Prior Year')}
          <div style={{padding:'14px'}}>
            <div style={{display:'flex',alignItems:'flex-end',gap:16,
              justifyContent:'center',marginBottom:14}}>
              {[
                {label:`${now.getFullYear()}`,val:ytdComparison.ytd,col:'#1e3060'},
                {label:`${now.getFullYear()-1}`,val:ytdComparison.prior,col:'#aab8cc'},
              ].map(({label,val,col})=>{
                const h=Math.round(val/Math.max(ytdComparison.ytd,ytdComparison.prior,1)*100);
                return (
                  <div key={label} style={{display:'flex',flexDirection:'column',
                    alignItems:'center',gap:4}}>
                    <span style={{fontSize:13,fontWeight:700,color:col}}>{val}</span>
                    <div style={{width:50,height:`${h}px`,background:col,
                      borderRadius:'4px 4px 0 0',minHeight:8,transition:'height 0.5s'}}/>
                    <span style={{fontSize:11,color:'#888'}}>{label}</span>
                  </div>
                );
              })}
            </div>
            <div style={{textAlign:'center',padding:'8px',borderRadius:6,
              background:ytdComparison.growth>=0?'#d4edda':'#f8d7da'}}>
              <div style={{fontSize:18,fontWeight:700,
                color:ytdComparison.growth>=0?'#1a6b42':'#b83232'}}>
                {ytdComparison.growth>=0?'▲':'▼'} {Math.abs(ytdComparison.growth)}%
              </div>
              <div style={{fontSize:11,color:'#555'}}>Year-over-year growth</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 4: Recent samples ── */}
      <div style={cardStyle}>
        {hdr('Recent Samples — Last 15')}
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead>
              <tr style={{background:'#f0f4ff'}}>
                {['Sample #','Work Order','Company','Test Type','Received',
                  'Status','Turnaround'].map(h=>(
                  <th key={h} style={{padding:'6px 10px',textAlign:'left',
                    borderBottom:'1px solid #e2e6f0',color:'#1e3060',fontWeight:600}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...filteredSamples].sort((a,b)=>b.id-a.id).slice(0,15).map(s=>{
                const ta = s.completed&&s.received&&s.dateCompleted
                  ? Math.round((new Date(s.dateCompleted)-new Date(s.received))/86400000)
                  : null;
                return (
                  <tr key={s.id} style={{borderBottom:'1px solid #f0f2f8'}}>
                    <td style={{padding:'5px 10px',fontWeight:700,fontFamily:'monospace'}}>{s.id}</td>
                    <td style={{padding:'5px 10px',fontFamily:'monospace',color:'#666'}}>{s.workOrder}</td>
                    <td style={{padding:'5px 10px',maxWidth:130,overflow:'hidden',
                      textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{companyName(s)}</td>
                    <td style={{padding:'5px 10px'}}>
                      <span style={{background:color(s.testType)+'22',color:color(s.testType),
                        padding:'1px 7px',borderRadius:10,fontSize:10,fontWeight:700}}>
                        {s.testType}
                      </span>
                    </td>
                    <td style={{padding:'5px 10px',color:'#666'}}>{s.received}</td>
                    <td style={{padding:'5px 10px'}}>
                      <span style={{background:s.completed?'#d4edda':'#fff3cd',
                        color:s.completed?'#155724':'#856404',
                        padding:'1px 7px',borderRadius:10,fontSize:10,fontWeight:700}}>
                        {s.completed?'Complete':'Pending'}
                      </span>
                    </td>
                    <td style={{padding:'5px 10px',fontWeight:600,
                      color:ta===null?'#ccc':ta<=3?'#1a6b42':ta<=7?'#e09900':'#b83232'}}>
                      {ta!==null?`${ta}d`:'—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Drill-down Modal ── */}
      <DrillModal modal={drillModal} onClose={()=>setDrillModal(null)}/>

    </div>
  );
}
